import { generateTaskFromTranscript } from './openai';
import { TaskCategory } from '@/contexts/AppContext';
import { parseISO, isValid } from 'date-fns';

// This is a mock AI service that simulates natural language processing
// In a real app, this would connect to a backend service or AI API

export interface ExtractedTaskInfo {
  title: string;
  category: TaskCategory;
  description: string;
  dueDate: string | null;
  isRecurring: boolean;
}

// Parse and validate date strings
const parseDueDate = (dateStr: string | null): string | null => {
  if (!dateStr) return null;
  
  try {
    // Parse the ISO date string
    const date = parseISO(dateStr);
    
    // Validate the date
    if (!isValid(date)) {
      console.error('Invalid date format received:', dateStr);
      return null;
    }
    
    // Return the ISO string
    return date.toISOString();
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

export const processTranscript = async (transcript: string): Promise<ExtractedTaskInfo> => {
  console.log('Processing transcript with OpenAI:', transcript);
  
  try {
    const taskInfo = await generateTaskFromTranscript(transcript);
    console.log('OpenAI response:', taskInfo);
    
    // Validate the response
    if (!taskInfo.title || !taskInfo.category) {
      throw new Error('Invalid task information received');
    }
    
    // Format the date properly
    const formattedDueDate = parseDueDate(taskInfo.dueDate);
    
    return {
      title: taskInfo.title,
      category: taskInfo.category as TaskCategory,
      description: taskInfo.description || '',
      dueDate: formattedDueDate,
      isRecurring: taskInfo.isRecurring || false
    };
  } catch (error) {
    console.error('Error in processTranscript:', error);
    throw new Error('Failed to process voice input. Please try again.');
  }
};

// Helper function to calculate days until next occurrence of a day of the week
function getNextDayOfWeek(dayOfWeek: number): number {
  const today = new Date().getDay();
  return (7 + dayOfWeek - today) % 7 || 7;
}

// Simulate AI processing delay
export const simulateProcessing = async (transcript: string): Promise<ExtractedTaskInfo> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(processTranscript(transcript));
    }, 1000); // 1 second delay to simulate processing
  });
};
